import type { OzetResponse } from "@/api/ozet";
import { ChartCard } from "@/components/ChartCard";
import { t } from "@/i18n";
import { fmtDateShort, fmtInt, fmtMoney } from "@/lib/utils";

interface CiroTrendiProps {
  data: OzetResponse;
}

/**
 * Günlük ciro trendi — gidişat kartının içinde (eski `gd-trend`): seçili
 * dönem düz mavi, önceki dönem kesikli gri; gün indeksine göre hizalanır
 * (backend sıra numarasını hizalar). Erişilebilir eş: `<details>` tablo.
 */
export function CiroTrendi({ data }: CiroTrendiProps) {
  const cur = data.satis.gunluk ?? [];
  const prev = data.satis.onceki_gunluk ?? [];
  const gun = data.donem.gun;
  const n = Math.max(cur.length, prev.length);

  if (n === 0) {
    return (
      <div data-testid="ciro-trendi">
        <h3 className="mb-1 font-display text-[15px] font-bold text-ink">{t("ozet.trend.baslik")}</h3>
        <p className="text-sm text-muted">{t("ozet.trend.veri_yok")}</p>
      </div>
    );
  }

  const labels = Array.from({ length: n }, (_, i) => {
    const iso = cur[i]?.gun ?? prev[i]?.gun ?? null;
    return iso ? fmtDateShort(iso) : t("ozet.trend.gun_etiketi", { n: String(i + 1) });
  });
  const curValues = labels.map((_, i) => cur[i]?.ciro ?? null);
  const prevValues = labels.map((_, i) => prev[i]?.ciro ?? null);
  const legCur = `Seçili dönem · ${gun} gün`;
  const legPrev = t("ozet.trend.onceki_n_gun", { n: String(gun) });

  return (
    <div data-testid="ciro-trendi">
      <h3 className="mb-1 font-display text-[15px] font-bold text-ink">{t("ozet.trend.baslik")}</h3>
      <ChartCard
        bare
        type="line"
        valueKind="money"
        height={230}
        labelEvery={Math.max(1, Math.ceil(n / 12))}
        data={{
          labels,
          datasets: [
            { label: legCur, data: curValues, borderColor: "#1A56DB", backgroundColor: "rgba(26,86,219,0.08)", fill: true },
            { label: legPrev, data: prevValues, borderColor: "#9CA3AF", backgroundColor: "#9CA3AF", borderDash: [6, 4], pointRadius: 2 },
          ],
        }}
      />
      <details className="mt-2">
        <summary className="min-h-[36px] cursor-pointer text-xs font-semibold text-muted hover:text-ink">{t("ozet.trend.tablo_ac")}</summary>
        <div className="mt-2 overflow-x-auto">
          <table className="tbl min-w-[480px] text-xs">
            <caption className="sr-only">{t("ozet.trend.tablo_baslik", { cur: legCur, prev: legPrev })}</caption>
            <thead>
              <tr>
                <th scope="col">{t("ozet.trend.kolon.gun")}</th>
                <th scope="col">{t("ozet.trend.kolon.tarih")}</th>
                <th scope="col" className="num">
                  {t("ozet.trend.kolon.ciro")}
                </th>
                <th scope="col" className="num">
                  {t("ozet.trend.kolon.siparis")}
                </th>
                <th scope="col">{t("ozet.trend.kolon.onceki_tarih")}</th>
                <th scope="col" className="num">
                  {t("ozet.trend.kolon.onceki_ciro")}
                </th>
                <th scope="col" className="num">
                  {t("ozet.trend.kolon.onceki_siparis")}
                </th>
              </tr>
            </thead>
            <tbody>
              {labels.map((_, i) => {
                const c = cur[i];
                const p = prev[i];
                return (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{c ? fmtDateShort(c.gun) : "—"}</td>
                    <td className="num">{c ? fmtMoney(c.ciro) : "—"}</td>
                    <td className="num">{c ? fmtInt(c.siparis) : "—"}</td>
                    <td>{p ? fmtDateShort(p.gun) : "—"}</td>
                    <td className="num">{p ? fmtMoney(p.ciro) : "—"}</td>
                    <td className="num">{p ? fmtInt(p.siparis) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}
